import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Modal } from '../../components/ui/common';
import { Icon } from '../../components/ui/icon';
import { toast } from '../../components/ui/toast';
import { api, errorMessage } from '../../lib/api';
import { useCan } from '../../lib/session';
import { qk } from './api';

const CONFIRM_WORD = 'החלפת מספר';

/**
 * "Change number": the OpenWA session is bound to the WhatsApp account that linked first, so linking a different
 * number needs a fresh session. POST /api/channels/:id/relink (channels.connect + TOTP + typed confirmation).
 */
export function RelinkButton({ channelId, phone, className = 'btn ghost sm' }: { channelId: string; phone: string | null; className?: string }) {
  const can = useCan();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const relink = useMutation({
    mutationFn: () => api<{ status: string; qr: string | null }>(`/api/channels/${channelId}/relink`, { body: { confirm, totpCode: code } }),
    onSuccess: () => {
      toast('החיבור הקודם נמחק. סורקים עכשיו מהטלפון של המספר הנכון.');
      setOpen(false);
      setConfirm('');
      setCode('');
      void qc.invalidateQueries({ queryKey: ['wa'] });
      void qc.invalidateQueries({ queryKey: qk.channels });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  if (!can('channels.connect')) return null;
  return (
    <>
      <button className={className} type="button" onClick={() => setOpen(true)}>
        <Icon name="refresh" />
        החלפת מספר
      </button>
      <Modal
        open={open}
        title="החלפת המספר המחובר"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button
              className="btn danger solid"
              disabled={relink.isPending || confirm.trim() !== CONFIRM_WORD || code.length < 6}
              onClick={() => relink.mutate()}
            >
              {relink.isPending ? 'מחליפים…' : 'מחיקת החיבור והצגת QR חדש'}
            </button>
            <button className="btn" onClick={() => setOpen(false)}>
              ביטול
            </button>
          </>
        }
      >
        <p>
          החיבור הנוכחי{phone ? (
            <>
              {' '}
              (<span className="ltr">{phone}</span>)
            </>
          ) : null}{' '}
          יימחק לגמרי, ויוצג QR חדש לסריקה מהטלפון של המספר הנכון.
        </p>
        <ul className="small muted mt">
          <li>קבוצות, אנשי קשר ושיחות שנאספו מהמספר הקודם יימחקו מהמערכת.</li>
          <li>קבוצות שכבר שויכו ללקוחות נשמרות, אבל יסומנו "לא חבר" עד שהמספר החדש יהיה בהן.</li>
          <li>בטלפון של המספר הקודם כדאי גם למחוק את המכשיר המקושר (וואטסאפ › מכשירים מקושרים).</li>
        </ul>
        <div className="field mt">
          <label htmlFor="relink-confirm">
            כדי לאשר, מקלידים: <b>{CONFIRM_WORD}</b>
          </label>
          <input className="input" id="relink-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        <div className="field mt">
          <label htmlFor="relink-code">קוד מאפליקציית Authenticator</label>
          <input
            className="input ltr"
            id="relink-code"
            inputMode="numeric"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s/g, '').slice(0, 20))}
          />
        </div>
      </Modal>
    </>
  );
}
